import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { InlineCopy } from '../InlineCopy';

// could be id, ik, or path depending on entity, interface requires specifiying type to
// hopefully prevent mistakes.
type Props = {
  type: 'id' | 'ik' | 'path' | 'key' | 'externalId';
  identifier: string;
  duration?: number;
};

// can't import this from assert lib bceause of craco so making a helper.
const assertUnreachable = (_: never) => {
  throw new Error('Unreachable');
};

const generateButtonName = (id: string) => `copy-${id}`;

/**
 * Search for a copy button given its ID, and trigger the click handler.
 * This is used for keyboard shortcuts to ensure preservation of functionality for
 * keyboard users.
 * @param entityId The ID of the entity for which to copy an ID.
 *  This should match the `id` field of `IdCopyButton`
 * @param parentElement The parent element within which to search for the button.
 *  The `IdCopyButton` must be within this element.
 */
export const clickCopyButton = (
  entityId: string,
  parentElement: ParentNode
) => {
  const buttonName = generateButtonName(entityId);
  // shamelessly lifted from
  // https://stackoverflow.com/questions/22830519/what-chars-needs-escaping-in-queryselector
  const escapedName = buttonName.replace(/["\\]/g, '\\$&');
  parentElement
    .querySelectorAll<HTMLButtonElement>(`button[name="${escapedName}"]`)
    .forEach((node) => node.click());
};

/**
 * A button that can be clicked to write the provided ID to the user's clipboard.
 * This button is unfocusable since it is often rendered only on hover, so
 * be sure you to provide a keyboard shortcut that calls `clickCopyButton` for keyboard-only users.
 */
export const IdCopyButton = ({ type, identifier, duration = 1200 }: Props) => {
  const [copyError, setCopyError] = useState<boolean>(false);
  const errorTimer = useRef<ReturnType<typeof setTimeout>>();
  const [showSuccessText, setShowSuccessText] = useState(false);

  const handleCopyError = () => {
    if (errorTimer.current) {
      clearTimeout(errorTimer.current);
      errorTimer.current = undefined;
    }

    setCopyError(true);
    errorTimer.current = setTimeout(() => {
      setCopyError(false);
    }, duration);
  };

  useEffect(
    () => () => {
      if (errorTimer.current) {
        clearTimeout(errorTimer.current);
      }
    },
    []
  );

  let buttonText = 'CONSOLE_ERROR';
  switch (type) {
    case 'id':
      buttonText = '#ID';
      break;
    case 'ik':
      buttonText = '#IK';
      break;
    case 'path':
      buttonText = '#PATH';
      break;
    case 'key':
      buttonText = '#KEY';
      break;
    case 'externalId':
      buttonText = '#ID';
      break;
    default:
      assertUnreachable(type);
  }

  return (
    <span
      className={clsx(
        showSuccessText ? 'inline' : 'hidden',
        // NavTable sets group and entity Summary sets group-one. Both of these components are rendering
        // essentially the same thing so I think there's an opportunity to refactor and consolidate the groups
        'group-hover:inline group-focus:inline group-one-hover:inline group-one-focus:inline'
      )}
    >
      <InlineCopy
        // We want to be able to trigger DOM element clicks programatically
        // (for keyboard shortcuts), but lookups by ID enforce uniqueness.
        // To allow multiple elements with the same "id", we can use `name` instead.
        name={generateButtonName(identifier)}
        duration={duration}
        textToCopy={identifier}
        onCopyChildren="Copied"
        onCopyError={handleCopyError}
        stopEventPropagationAndDefault
        preventFocus
        onCopy={() => setShowSuccessText(true)}
        onCopySuccess={() => setShowSuccessText(false)}
        data-testid="table-hover-detail"
      >
        {copyError ? (
          <span className="text-orange">Copy Failed</span>
        ) : (
          buttonText
        )}
      </InlineCopy>
    </span>
  );
};
